import React, { useState, useContext } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { AuthContext, API } from '../App';
import { ChevronDown, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const ACCESS_TYPES = [
  { value: 'cv_fix_access', label: 'CV Fix Access (1 download)' },
  { value: 'interview_pack_access', label: 'Interview Pack Access' },
  { value: 'templates_access', label: 'Job Apply Kit / Templates Access' },
  { value: 'pro_access', label: 'Pro Access (full features)' },
  { value: 'beta_tester', label: 'Beta Tester (Pro + Interview + Templates)' },
];

export default function AdminAccessPage() {
  const { user } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [accessType, setAccessType] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleGrant = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Enter user email'); return; }
    if (!accessType) { toast.error('Select access type'); return; }

    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/admin/grant-access`, {
        email: email.trim(),
        access_types: [accessType],
        note: note.trim() || null
      });
      setResult(res.data);
      toast.success(res.data.message);
      setEmail('');
      setAccessType('');
      setNote('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant access');
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'admin') {
    return <div className="p-8 text-center text-gray-500">Admin access required.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-lg mx-auto">
        <h1 className="text-2xl font-bold mb-6" data-testid="admin-access-title">Grant User Access</h1>

        <Card className="p-6" data-testid="admin-access-form">
          <form onSubmit={handleGrant} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Email *</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]"
                data-testid="admin-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Access Type *</label>
              <div className="relative">
                <select
                  value={accessType} onChange={e => setAccessType(e.target.value)}
                  className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126] appearance-none"
                  data-testid="admin-access-select"
                >
                  <option value="">Select access type</option>
                  {ACCESS_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
                <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Note <span className="text-gray-400">(optional)</span></label>
              <input
                type="text" value={note} onChange={e => setNote(e.target.value)}
                placeholder="e.g. WhatsApp payment received"
                className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]"
                data-testid="admin-note-input"
              />
            </div>

            <Button type="submit" className="w-full btn-uae-red" disabled={loading} data-testid="admin-grant-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Granting...</> : 'Grant Access'}
            </Button>
          </form>
        </Card>

        {result && (
          <Card className="p-5 mt-4 bg-green-50 border-green-200" data-testid="admin-success-msg">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#009639] mt-0.5" />
              <div>
                <p className="font-medium text-[#009639]">{result.message}</p>
                {result.granted?.map(g => (
                  <p key={g.entitlement_id} className="text-xs text-gray-500 mt-1">ID: {g.entitlement_id}</p>
                ))}
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
